import React, { useEffect, useState } from "react";
import { Card, Modal, Table, Typography, Tooltip, message, Input, List, Spin, Empty, Popover, Button, Row, Col, Tabs, Tag, Progress } from "antd";
import { RobotOutlined, InfoCircleOutlined, ArrowRightOutlined, InboxOutlined, FieldTimeOutlined, AlertOutlined, ShopOutlined, ExclamationCircleOutlined, DatabaseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";
import GreetingCard from "../../components/GreetingCard";

const { Title, Text } = Typography;

// ====== NEW RACK STRUCTURE (A-S, variable columns, 3 levels) ======
// Configuration: Row -> max column count
const ROW_CONFIG = {
  A: 52, B: 52, C: 52, D: 52, E: 52, F: 52, G: 52, H: 52,
  I: 52, J: 52, K: 52, L: 52, M: 52, N: 52, O: 52, P: 52,
  Q: 37, R: 37, S: 41
};

// Rows displayed from bottom (A) to top (S)
const ROWS = ["S", "R", "Q", "P", "O", "N", "M", "L", "K", "J", "I", "H", "G", "F", "E", "D", "C", "B", "A"];
const LEVELS = [1, 2, 3];

// Generate all rack positions for a given level
const makeRacks = (level = 1) => {
  const data = [];
  for (const row of Object.keys(ROW_CONFIG)) {
    const maxCol = ROW_CONFIG[row];
    for (let col = 1; col <= maxCol; col++) {
      data.push({
        id: `${row}.${col}.${level}`,
        row,
        col,
        level,
        type: "rack",
      });
    }
  }
  return data;
};

// Helper: build all lot IDs for a rack position (all 3 levels)
const buildLotsForRack = (row, col) => {
  return LEVELS.map((level) => `${row}.${col}.${level}`);
};

// Helper: get max column for a row
const getMaxCol = (row) => ROW_CONFIG[row] || 52;

/**
 * AI Summary Popover Component
 * Fetches and displays a summary for a given item.
 */
const AISummaryPopover = ({ item }) => {
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchSummary = async () => {
    if (summary) return; // already fetched
    setLoading(true);
    try {
      // Use the newly created AI route
      const res = await axiosInstance.post("/ai/summary", {
        type: item.type,
        id: item.id
      });
      setSummary(res.data?.summary || "Tidak ada ringkasan.");
    } catch (err) {
      console.error(err);
      setSummary("Gagal memuat ringkasan AI.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover
      trigger="click"
      title={<span><RobotOutlined style={{ marginRight: 5 }} /> Smart Resume</span>}
      content={
        <div style={{ width: 300, fontSize: 13 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /> Menganalisa...</div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: summary.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') }} />
          )}
        </div>
      }
      onOpenChange={(visible) => {
        if (visible) fetchSummary();
      }}
    >
      <Button
        type="text"
        size="small"
        style={{ color: '#1890ff', fontSize: 12, display: 'flex', alignItems: 'center' }}
        onClick={(e) => e.stopPropagation()} // Prevent triggering parent onClick
      >
        <RobotOutlined /> AI Summary
      </Button>
    </Popover>
  );
};

/**
 * Greeting Card Component
 */
/**
 * AI Summary Card Component (Auto Load)
 */
const AISummaryCard = ({ item }) => {
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.post("/ai/summary", {
          type: item.type,
          id: item.id
        });
        setSummary(res.data?.summary || "Tidak ada ringkasan.");
      } catch (err) {
        console.error(err);
        setSummary("Gagal memuat ringkasan AI.");
      } finally {
        setLoading(false);
      }
    };
    if (item && item.id) fetchSummary();
  }, [item]);

  return (
    <div style={{ padding: 10 }}>
      {loading ? <div style={{ textAlign: "center" }}><Spin /> Menganalisa...</div> :
        <div dangerouslySetInnerHTML={{ __html: summary.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') }} />}
    </div>
  );
};

export default function WarehouseLayout() {
  const history = useHistory();
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeLevel, setActiveLevel] = useState(1); // Rack level selector (1, 2, 3)
  const [showLotSementara, setShowLotSementara] = useState(false); // Track LOT SEMENTARA view

  // ====== MODAL STOCK DETAILS ======
  const [modalVisible, setModalVisible] = useState(false);
  // stockDetails & loadingStock are declared later as stockData/loadingStock.
  // We will rename usage in fetchStockDetails below to match.



  // Global Search State
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Debounce Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchText && searchText.length >= 2) {
        handleGlobalSearch(searchText);
      } else {
        setSearchResults([]);
      }
    }, 600);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText]);

  const handleGlobalSearch = async (value) => {
    if (!value || value.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axiosInstance.get("/global-search", {
        params: { q: value },
      });
      setSearchResults(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  // data incoming utk area loading
  const [incomingList, setIncomingList] = useState([]);
  const [loadingIncoming, setLoadingIncoming] = useState(false);

  // data outgoing utk area transit
  const [transitList, setTransitList] = useState([]);
  const [loadingTransit, setLoadingTransit] = useState(false);

  // data stok utk modal rak/CL
  const [stockData, setStockData] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [occupiedLots, setOccupiedLots] = useState({});

  // Dashboard Stats
  const [dashboardStats, setDashboardStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);

  const fetchStockDetails = async () => {
    setLoadingStock(true);
    try {
      const response = await axiosInstance.get('/item-stock?limit=100');
      const items = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setStockData(items); // Updated to match existing state
      setModalVisible(true);
    } catch (error) {
      console.error("Failed to fetch stock details", error);
      message.error("Gagal mengambil detail stok");
    } finally {
      setLoadingStock(false);
    }
  };

  const stockColumns = [
    { title: 'Kode Barang', dataIndex: 'item_code', key: 'item_code' },
    { title: 'Nama Barang', dataIndex: 'item_name', key: 'item_name' },
    { title: 'Jumlah Kemasan', dataIndex: 'jumlah_kemasan', key: 'jumlah_kemasan' },
    { title: 'Masa Timbun', dataIndex: 'masa_timbun', key: 'masa_timbun', render: (val) => `${val} Hari` },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name' },
  ];

  // tabel untuk item-stock
  const columnsItemStock = [
    { title: "SERI", dataIndex: "seri", key: "seri", width: 60 },
    { title: "NOPEN", dataIndex: "nopen", key: "nopen", width: 100 },
    { title: "KODE BARANG", dataIndex: "item_code", key: "item_code" },
    { title: "NAMA BARANG", dataIndex: "item_name", key: "item_name" },
    {
      title: "LOT",
      dataIndex: "lokasi_lot",
      key: "lokasi_lot",
      width: 90,
    },
    {
      title: "JUMLAH KEMASAN",
      dataIndex: "jumlah_kemasan",
      key: "jumlah_kemasan",
      width: 130,
      align: "right",
    },
    {
      title: "MASA TIMBUN",
      dataIndex: "masa_timbun",
      key: "masa_timbun",
      width: 110,
      align: "center",
      render: (val) => `${val || 0} Hari`,
    },
  ];

  // ====== FETCH FUNCTIONS ======
  const fetchIncoming = async () => {
    setLoadingIncoming(true);
    try {
      const res = await axiosInstance.get("/incoming", { params: { stripping: 1 } });
      let list = res.data?.data || [];
      const user = getUser();
      if (user && user.id_level === 7) {
        list = list.filter(i => i.id_customer === user.username || i.nama_customer === user.username);
      }
      const filtered = list.filter((inc) => inc.stripping === 0 || inc.stripping === 2);
      setIncomingList(filtered);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingIncoming(false);
    }
  };

  const fetchTransit = async () => {
    setLoadingTransit(true);
    try {
      const res = await axiosInstance.get("/outgoing", { params: { status: "DRAFT", limit: 100 } });
      let list = res.data?.data || [];
      const user = getUser();
      if (user && user.id_level === 7) {
        list = list.filter(i => i.id_customer === user.username || i.nama_customer === user.username);
      }
      setTransitList(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTransitDetails(false); // Fix typo in original: setLoadingTransit(false)
      setLoadingTransit(false);
    }
  };

  const fetchOccupiedLots = async () => {
    try {
      const res = await axiosInstance.get("/item-stock/occupied-lots");
      // Convert to map: { 'LOT_ID': max_age }
      const map = {};
      const list = res.data || [];
      list.forEach((item) => {
        map[item.lokasi_lot] = item.max_age;
      });
      setOccupiedLots(map);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchItemStockByLots = async (lots = []) => {
    setLoadingStock(true);
    setStockData([]);
    try {
      const res = await axiosInstance.get("/item-stock/all-stock");
      let allStock = res.data || [];
      const user = getUser();
      if (user && user.id_level === 7) {
        allStock = allStock.filter(i =>
          i.id_customer === user.username || i.nama_customer === user.username
        );
      }
      const filtered = allStock.filter((item) => lots.includes(item.lokasi_lot));
      setStockData(filtered);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat stok barang");
    } finally {
      setLoadingStock(false);
    }
  };

  const fetchDashboardStats = async () => {
    setLoadingStats(true);
    try {
      const res = await axiosInstance.get("/item-stock/stats/summary");
      setDashboardStats(res.data || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  };

  // INITIAL LOAD
  useEffect(() => {
    fetchIncoming();
    fetchTransit();
    fetchOccupiedLots();
    fetchDashboardStats();
    // eslint-disable-next-line
  }, []);

  // ====== CLICK HANDLERS ======
  const handleClickRack = (rack) => {
    // New format: rack.id is already like "A.1.1"
    // Get all levels for this position
    const allLevelsLots = buildLotsForRack(rack.row, rack.col);
    setSelected({
      id: rack.id,
      type: "rack",
      lots: allLevelsLots,
      row: rack.row,
      col: rack.col
    });
    setOpen(true);
    fetchItemStockByLots(allLevelsLots);
  };

  const handleClickIncoming = (inc) => {
    setSelected({ selectedType: "incoming", ...inc });
    setOpen(true);
  };

  const handleClickTransit = (out) => {
    setSelected({ selectedType: "transit", ...out });
    setOpen(true);
    fetchTransitDetails(out.id_outgoing);
  };

  // ====== NAVIGATION HANDLER ======
  const handleSearchResultClick = (item) => {
    if (item.type === 'Incoming') {
      history.push(`/inventory/updateincoming/${item.id}`);
    } else if (item.type === 'Outgoing') {
      history.push(`/inventory/updateoutgoing/${item.id}`);
    } else if (item.type === 'Stripping') {
      history.push(`/inventory/updatetallysheet/${item.id}`);
    } else if (item.type === 'Loading') {
      history.push(`/inventory/updatemuat/${item.id}`);
    } else if (item.type === 'Stock') {
      history.push('/monitoring/stock');
      message.info("Dialihkan ke Monitoring Stock. Silakan filter item code: " + item.title);
    } else if (item.type === 'Adjustment') {
      history.push('/other/adjustment');
      message.info("Dialihkan ke Adjustment. Silakan cari nomor: " + item.title);
    } else if (item.type === 'StockOpname') {
      history.push('/other/opname');
      message.info("Dialihkan ke Stock Opname. Silakan cari nomor: " + item.title);
    } else if (item.type === 'PindahLot') {
      history.push('/other/pindah-lot');
      message.info("Dialihkan ke Pindah Lot. Silakan cari nomor: " + item.title);
    } else if (item.type === 'Pemusnahan') {
      history.push(`/inventory/updatepemusnahan/${item.id}`);
    } else if (item.type === 'Item') {
      history.push('/referensi/komoditas');
      message.info("Dialihkan ke Master Barang. Silakan cari barang: " + item.title);
    } else if (item.type === 'Company') {
      history.push(`/referensi/kontak/edit/${item.id}`);
      message.info("Membuka detail perusahaan: " + item.title);
    } else if (item.type === 'User') {
      setSelected({ selectedType: 'user_detail', ...item });
      setOpen(true);
    } else {
      message.info(`Topik: ${item.type} - ${item.title}`);
    }
  };

  // ====== STATE UNTUK DETAIL TRANSIT ======
  const [transitDetails, setTransitDetails] = useState([]);
  const [loadingTransitDetails, setLoadingTransitDetails] = useState(false);

  const fetchTransitDetails = async (idOutgoing) => {
    setLoadingTransitDetails(true);
    setTransitDetails([]);
    try {
      const res = await axiosInstance.get(`/outgoing/${idOutgoing}/with-details`);
      let dets = res.data?.details || [];
      const user = getUser();
      if (user && user.id_level === 7) {
        // usually transit details are already specific to the outgoing header, which might be tricky if we don't have owner info in details.
        // Assuming details *might* have item_code which can be mapped to owner? 
        // Or checking header owner?
        // Since we filtered the outgoing list (which we haven't done yet, let's do that next), this detail fetch is only triggered by clicking a filtered item.
        // So this might be redundant but safe.
        // However, if we don't have fields, we might break it. 
        // Let's assume we filter the LIST (fetchTransit) and that prevents clicking unauthorized items.
      }
      setTransitDetails(dets);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail barang transit");
    } finally {
      setLoadingTransitDetails(false);
    }
  };

  // ====== DASHBOARD DETAILS STATE ======
  const [detailsList, setDetailsList] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [capacityData, setCapacityData] = useState({ used: [], empty: [] });

  const fetchCategoryDetails = async (category) => {
    setLoadingDetails(true);
    setDetailsList([]);
    try {
      const res = await axiosInstance.get("/item-stock/details", { params: { category } });
      setDetailsList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail");
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchCapacityDetails = async () => {
    setLoadingDetails(true);
    setCapacityData({ used: [], empty: [] });
    try {
      const res = await axiosInstance.get("/item-stock/capacity-details");
      setCapacityData(res.data || { used: [], empty: [] });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail kapasitas");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCardClick = (type) => {
    if (type === 'total') return;

    setSelected({ selectedType: 'dashboard_stat', subtype: type });
    setOpen(true);

    if (type === 'capacity') {
      fetchCapacityDetails();
    } else {
      fetchCategoryDetails(type);
    }
  };

  // ====== RENDER HELPERS ======
  const docDate = selected?.document_date ? dayjs(selected.document_date).format("DD MMMM YYYY") : "-";

  const renderModalHeader = () => {
    if (!selected) return null;
    if (selected.selectedType === "incoming" || selected.selectedType === "transit") {
      const isTransit = selected.selectedType === "transit";
      return (
        <div style={{ paddingBottom: 16, borderBottom: "1px solid #eee", marginBottom: 16 }}>
          {selected.selectedType === "incoming" && (
            <>
              <Text strong>Nomor BPB:&nbsp;</Text>
              <br />
              {selected.bpb_number || "-"}
              <br />
              <Text strong>Tanggal BPB:&nbsp;</Text>
              <br />
              {selected.bpb_date ? dayjs(selected.bpb_date).format("DD MMMM YYYY") : "-"}
              <br />
            </>
          )}

          <Text strong>{isTransit ? "Pemilik Barang:" : "Importir:"}&nbsp;</Text>
          <br />
          {selected.nama_pemilik || selected.nama_customer || "-"}
          <br />
          {!isTransit && (
            <>
              <Text strong>Pemasok:&nbsp;</Text>
              <br />
              {selected.id_contact || "-"}
              <br />
            </>
          )}

          <Text strong>Nomor Dokumen (NOPEN):&nbsp;</Text>
          <br />
          {selected.document_number || "-"}
          <br />
          <Text strong>Tanggal Dokumen:&nbsp;</Text>
          <br />
          {docDate}
          <br />
        </div>
      );
    }

    if (selected.selectedType === 'user_detail') {
      return (
        <div style={{ marginBottom: 12 }}>
          <Text strong>REGISTER ID :&nbsp;&nbsp;&nbsp;</Text>
          {selected.id}
        </div>
      );
    }

    return null; // Header info removed per user request
  };

  const renderModalBody = () => {
    if (!selected) return null;

    if (selected.selectedType === "dashboard_stat") {
      if (selected.subtype === "capacity") {
        const columnsUsed = [
          { title: "Lot", dataIndex: "lot", key: "lot", width: 80 },
          { title: "Total Item", dataIndex: "total_items", key: "total_items", align: "right" },
          { title: "Total Qty", dataIndex: "total_qty", key: "total_qty", align: "right" },
        ];
        const columnsEmpty = [
          { title: "Lot", dataIndex: "lot", key: "lot" },
          { title: "Status", dataIndex: "status", key: "status", render: () => <Tag color="green">Empty</Tag> }
        ];

        return (
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab={`Terpakai (${capacityData.used.length})`} key="1">
              <Table
                dataSource={capacityData.used}
                columns={columnsUsed}
                loading={loadingDetails}
                pagination={{
                  pageSize: 10,
                  showTotal: (total) => `Total ${total} lot terpakai`,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
                size="small"
                rowKey="lot"
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={`Kosong (${capacityData.empty.length})`} key="2">
              <Table
                dataSource={capacityData.empty}
                columns={columnsEmpty}
                loading={loadingDetails}
                pagination={{
                  pageSize: 20,
                  showTotal: (total) => `Total ${total} lot kosong`,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100', '200']
                }}
                size="small"
                rowKey="lot"
              />
            </Tabs.TabPane>
          </Tabs>
        );
      } else {
        // List Item (Slow, Attention, Critical)
        const columnsDetails = [
          { title: "Kode Barang", dataIndex: "item_code", key: "item_code" },
          { title: "Nama Barang", dataIndex: "item_name", key: "item_name" },
          { title: "Nopen", dataIndex: "nopen", key: "nopen" },
          { title: "Tgl Nopen", dataIndex: "tanggal_nopen", key: "tanggal_nopen", render: (d) => d ? dayjs(d).format("DD MMM YYYY") : "-" },
          { title: "Masa Timbun", dataIndex: "masa_timbun", key: "masa_timbun", render: (v) => `${v} Hari` },
          { title: "Lot Asal", dataIndex: "lot_asal", key: "lot_asal" },
        ];

        return (
          <Table
            dataSource={detailsList}
            columns={columnsDetails}
            loading={loadingDetails}
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Total ${total} item`,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            size="small"
            rowKey={(r) => r.item_code + r.nopen + r.lot_asal}
          />
        );
      }
    }

    if (selected.selectedType === "incoming") {
      // Incoming lists are simple
      return null;
    }

    if (selected.selectedType === "transit") {
      const columnsTransit = [
        { title: "Kode Barang", dataIndex: "item_code", key: "item_code" },
        { title: "Nama Barang", dataIndex: "item_name", key: "item_name" },
        {
          title: "Jumlah Kemasan",
          key: "kemasan",
          render: (_, r) => `${r.total_kemasan || 0} ${r.kemasan || ''}`
        },
        { title: "Lokasi Lot", dataIndex: "lot", key: "lot" }
      ];

      return (
        <>
          <Title level={5} style={{ marginTop: 8 }}>
            <i> Detail Barang (Transit)</i>
          </Title>
          <Table
            dataSource={transitDetails}
            columns={columnsTransit}
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Total ${total} item`,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50']
            }}
            bordered
            size="small"
            loading={loadingTransitDetails}
            rowKey="id_outgoing_detail"
          />
        </>
      );
    }

    // User Detail
    if (selected.selectedType === 'user_detail') {
      return (
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#e6f7ff', margin: '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 32, color: '#1890ff' }}>{selected.title?.charAt(0)}</span>
            </div>
            <Title level={3}>{selected.title}</Title>
            <Text type="secondary">{selected.description}</Text>
          </div>

          <Card style={{ marginTop: 20, textAlign: 'left', border: '1px solid #f0f0f0' }} title="AI Analysis">
            <div style={{ padding: 10 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 10, textAlign: 'center' }}>
                Analisa kebiasaan dan aktivitas user menggunakan AI:
              </Text>
              <AISummaryCard item={{ type: 'User', id: selected.id }} />
            </div>
          </Card>
        </div>
      );
    }

    return (
      <>
        <Title level={5} style={{ marginTop: 8 }}>
          <i> Detail Stok Barang</i>
        </Title>
        <Table
          dataSource={stockData}
          columns={columnsItemStock}
          pagination={{
            pageSize: 10,
            showTotal: (total) => `Total ${total} item`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            className: 'modern-pagination',
            showQuickJumper: true,
            locale: {
              jump_to: 'Halaman',
              page: '',
            }
          }}
          bordered
          size="small"
          loading={loadingStock}
          scroll={{ x: 700 }}
          onRow={(record) => {
            let color = "inherit";
            let fontWeight = "normal";
            // Highlight text color based on age
            if (record.masa_timbun > 730) { color = "#ff4d4f"; fontWeight = "bold"; } // Critical
            else if (record.masa_timbun > 365) { color = "#fa8c16"; fontWeight = "bold"; } // Attention
            else if (record.masa_timbun > 180) { color = "#faad14"; fontWeight = "bold"; } // Slow Moving

            return { style: { color, fontWeight } };
          }}
        />
      </>
    );
  };

  const modalTitle = (() => {
    if (!selected) return "Detail";
    if (selected.selectedType === "incoming") return `Detail Incoming - ${selected.document_number || "-"}`;
    if (selected.selectedType === "transit") return `Detail Transit - ${selected.document_number || "-"}`;
    if (selected.selectedType === "dashboard_stat") {
      switch (selected.subtype) {
        case 'slow_moving': return "Detail Slow Moving (6 bln - 1 thn)";
        case 'attention': return "Detail Attention (1 - 2 thn)";
        case 'critical': return "Detail Critical (> 2 thn)";
        case 'capacity': return "Detail Kapasitas Gudang";
        default: return "Detail";
      }
    }
    if (selected.selectedType === 'user_detail') return "User Profile";
    if (selected.type === 'lot_sementara') return "Detail LOT SEMENTARA (LS.99)";
    return `Detail Lokasi ${selected.id || "-"}`;
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f9fafc", fontFamily: "Poppins, sans-serif", padding: "16px", paddingBottom: 80 }}>
      {/* ================== GREETING CARD ================== */}
      <GreetingCard />

      {/* ================== GLOBAL SEARCH ================== */}
      {/* ================== GLOBAL SEARCH ================== */}
      <div style={{ marginBottom: 16, position: "relative", zIndex: 50 }}>
        <Input.Search
          placeholder="Cari Dokumen, Barang, Rak, dll..."
          allowClear
          enterButton="Cari"
          size="large"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (!e.target.value) setSearchResults([]);
          }}
          onSearch={handleGlobalSearch}
        />

        {(searchText && searchText.length >= 2) && (
          <div style={{
            marginTop: 4,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 4,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            maxHeight: 400,
            overflowY: "auto",
            padding: 8,
            zIndex: 9999, // Ensure this sits on top of everything
            position: "absolute",
            width: "100%",
            left: 0
          }}>
            {searching ? (
              <div style={{ textAlign: "center", padding: 10 }}><Spin size="small" /> Mencari...</div>
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={searchResults}
                renderItem={item => (
                  <List.Item
                    style={{ padding: "8px 12px", borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center' }}
                    actions={[
                      <AISummaryPopover item={item} key="ai" />
                    ]}
                  >
                    <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => handleSearchResultClick(item)}>
                      <List.Item.Meta
                        title={<Text strong>[{item.type}] {item.title}</Text>}
                        description={item.description}
                      />
                    </div>
                  </List.Item>
                )}
                locale={{
                  emptyText: (
                    <div style={{ padding: 16, textAlign: "center" }}>
                      <Text type="secondary" strong style={{ fontSize: 14 }}>
                        Maaf, data yang Anda cari tidak dapat ditemukan.
                      </Text>
                      <br /><br />
                      <div style={{ textAlign: "left", display: "inline-block", background: "#fafafa", padding: 10, borderRadius: 6 }}>
                        <Text strong>Tips:</Text>
                        <ul style={{ paddingLeft: 20, marginTop: 5, marginBottom: 0, color: "#666" }}>
                          <li>Cek ejaan kata kunci.</li>
                          <li>Coba cari Nomor Dokumen / Kode Barang.</li>
                        </ul>
                      </div>
                    </div>
                  )
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* ================== STATS INFOGRAPHICS ================== */}
      {/* ================== STATS INFOGRAPHICS ================== */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Card 1: Total Items */}
        <Col xs={24} sm={12} md={6}>
          <Card bodyStyle={{ padding: "16px", display: "flex", alignItems: "center", cursor: 'pointer' }} hoverable onClick={() => fetchStockDetails()}>
            <div style={{ background: "#e6f7ff", padding: 12, borderRadius: 8, marginRight: 16 }}>
              <InboxOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Total Item Stok</Text>
              <Title level={4} style={{ margin: 0 }}>{loadingStats ? <Spin size="small" /> : (dashboardStats.totalItems || 0)}</Title>
            </div>
          </Card>
        </Col>

        {/* Card 2: Slow Moving */}
        <Col xs={24} sm={12} md={6}>
          <Card bodyStyle={{ padding: "16px", display: "flex", alignItems: "center" }} hoverable onClick={() => handleCardClick('slow_moving')}>
            <div style={{ background: "#fffbe6", padding: 12, borderRadius: 8, marginRight: 16 }}>
              <FieldTimeOutlined style={{ fontSize: 24, color: "#faad14" }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Slow Moving (6-12 Bln)</Text>
              <Title level={4} style={{ margin: 0 }}>{loadingStats ? <Spin size="small" /> : (dashboardStats.slowMoving || 0)}</Title>
            </div>
          </Card>
        </Col>

        {/* Card 3: Attention */}
        <Col xs={24} sm={12} md={6}>
          <Card bodyStyle={{ padding: "16px", display: "flex", alignItems: "center" }} hoverable onClick={() => handleCardClick('attention')}>
            <div style={{ background: "#fff7e6", padding: 12, borderRadius: 8, marginRight: 16 }}>
              <ExclamationCircleOutlined style={{ fontSize: 24, color: "#fa8c16" }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Attention (1-2 Thn)</Text>
              <Title level={4} style={{ margin: 0 }}>{loadingStats ? <Spin size="small" /> : (dashboardStats.attention || 0)}</Title>
            </div>
          </Card>
        </Col>

        {/* Card 4: Critical */}
        <Col xs={24} sm={12} md={6}>
          <Card bodyStyle={{ padding: "16px", display: "flex", alignItems: "center" }} hoverable onClick={() => handleCardClick('critical')}>
            <div style={{ background: "#fff1f0", padding: 12, borderRadius: 8, marginRight: 16 }}>
              <AlertOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Critical ({'>'}2 Thn)</Text>
              <Title level={4} style={{ margin: 0 }}>{loadingStats ? <Spin size="small" /> : (dashboardStats.critical || 0)}</Title>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ================== MAIN CONTENT ================== */}
      <Card
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
        bodyStyle={{ flex: 1, padding: 0 }}
      >
        <Row style={{ height: "100%", margin: 0 }}>

          {/* RAK (Left/Main) - With Level Tabs */}
          <Col xs={24} lg={18} xl={20} style={{ borderRight: "1px solid #f0f0f0", paddingBottom: 20 }}>
            <div style={{ padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                <Title level={5} style={{ margin: 0 }}>GUDANG PASIFIK - POSISI RAK</Title>

                {/* Level Selector Tabs */}
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {LEVELS.map((level) => (
                    <Button
                      key={level}
                      type={activeLevel === level && !showLotSementara ? "primary" : "default"}
                      size="small"
                      onClick={() => {
                        setActiveLevel(level);
                        setShowLotSementara(false);
                      }}
                      style={{
                        minWidth: 70,
                        fontWeight: activeLevel === level && !showLotSementara ? 700 : 400
                      }}
                    >
                      Lantai {level}
                    </Button>
                  ))}

                  {/* LOT SEMENTARA Button */}
                  <Button
                    type={showLotSementara ? "primary" : "default"}
                    size="small"
                    onClick={() => {
                      setShowLotSementara(true);
                      setSelected({
                        id: "LS.99",
                        type: "lot_sementara",
                        lots: ["LS.99"]
                      });
                      setOpen(true);
                      fetchItemStockByLots(["LS.99"]);
                    }}
                    style={{
                      minWidth: 100,
                      fontWeight: showLotSementara ? 700 : 400,
                      background: showLotSementara ? "#faad14" : undefined,
                      borderColor: showLotSementara ? "#faad14" : undefined
                    }}
                  >
                    LOT SEMENTARA
                  </Button>
                </div>
              </div>

              {/* Legend - Moved to top */}
              <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap", fontSize: 11 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 12, height: 12, background: "#f0f0f0", border: "1px solid #ccc", borderRadius: 2 }} />
                  <span>Kosong</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 12, height: 12, background: "#52c41a", borderRadius: 2 }} />
                  <span>Normal</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 12, height: 12, background: "#faad14", borderRadius: 2 }} />
                  <span>Slow</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 12, height: 12, background: "#fa8c16", borderRadius: 2 }} />
                  <span>Attention</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 12, height: 12, background: "#ff4d4f", borderRadius: 2 }} />
                  <span>Critical</span>
                </div>
              </div>

              {/* Rack Grid */}
              <div style={{ overflowX: "auto", paddingBottom: 8 }}>
                <div style={{ minWidth: 1200 }}>
                  {ROWS.map((row) => {
                    const maxCol = getMaxCol(row);
                    const racksPerRow = [];

                    // Generate racks for current row and level
                    for (let col = maxCol; col >= 1; col--) {
                      racksPerRow.push({
                        id: `${row}.${col}.${activeLevel}`,
                        row,
                        col,
                        level: activeLevel,
                        type: "rack"
                      });
                    }

                    return (
                      <div key={row} style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(52, 1fr)`,
                            gap: "1px",
                            flex: 1
                          }}
                        >
                          {/* Fill empty cells if row has less than 52 cols */}
                          {Array.from({ length: 52 }, (_, idx) => {
                            const colNum = 52 - idx;
                            const rackId = `${row}.${colNum}.${activeLevel}`;
                            const isValid = colNum <= maxCol;

                            if (!isValid) {
                              // Empty placeholder for alignment
                              return <div key={`empty-${row}-${colNum}`} style={{ height: 22 }} />;
                            }

                            // Check occupancy for CURRENT LEVEL ONLY
                            const currentLotId = `${row}.${colNum}.${activeLevel}`;
                            let maxAge = -1;
                            let isOccupied = false;

                            if (Object.prototype.hasOwnProperty.call(occupiedLots, currentLotId)) {
                              isOccupied = true;
                              maxAge = occupiedLots[currentLotId];
                            }

                            // Color based on age
                            let bgColor = "#f0f0f0";
                            if (isOccupied) {
                              if (maxAge > 730) bgColor = "#ff4d4f";        // Critical
                              else if (maxAge > 365) bgColor = "#fa8c16";   // Attention
                              else if (maxAge > 180) bgColor = "#faad14";   // Slow Moving
                              else bgColor = "#52c41a";                     // Normal
                            }

                            return (
                              <Tooltip key={rackId} title={`${row}.${colNum}.${activeLevel}`}>
                                <div
                                  onClick={() => handleClickRack({ id: rackId, row, col: colNum, level: activeLevel })}
                                  style={{
                                    height: 22,
                                    background: bgColor,
                                    border: "1px solid #ccc",
                                    borderRadius: 2,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    color: isOccupied ? "#fff" : "#666",
                                    fontSize: 9,
                                    lineHeight: "20px",
                                    fontWeight: isOccupied ? 600 : 400
                                  }}
                                >
                                  {colNum}
                                </div>
                              </Tooltip>
                            );
                          })}
                        </div>
                        <div style={{ width: 24, marginLeft: 6, textAlign: "center", fontWeight: "bold", color: "#333", fontSize: 12 }}>
                          {row}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Col>

          {/* TRANSIT & LOADING AREA (Right) */}
          <Col xs={24} lg={6} xl={4} style={{ background: "repeating-linear-gradient(-45deg,#f8f8f8,#f8f8f8 25px,#f0f0f0 25px,#f0f0f0 50px)" }}>
            <div style={{ padding: 12 }}>
              {/* CAPACITY CARD */}
              <Card
                hoverable
                onClick={() => handleCardClick('capacity')}
                style={{
                  marginBottom: 16,
                  background: "linear-gradient(to right bottom, #f6ffed, #ffffff)",
                  border: "1px solid #b7eb8f",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
                bodyStyle={{
                  padding: "10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center"
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, width: '100%', justifyContent: 'center' }}>
                  <DatabaseOutlined style={{ color: '#52c41a', marginRight: 8, fontSize: 18 }} />
                  <Text strong style={{ color: "#389e0d", fontSize: 13 }}>Kapasitas Gudang</Text>
                </div>

                <Progress
                  type="dashboard"
                  percent={dashboardStats.occupancyRate || 0}
                  width={80}
                  gapDegree={30}
                  strokeColor={{ '0%': '#87d068', '100%': '#52c41a' }}
                  format={percent => <span style={{ color: '#389e0d', fontSize: 16, fontWeight: 'bold' }}>{percent}%</span>}
                />

                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 10 }}>Terpakai / Total (Lot)</Text>
                  <br />
                  {loadingStats ? <Spin size="small" /> : (
                    <Text strong style={{ fontSize: 14, color: '#237804' }}>
                      {dashboardStats.occupiedLots || 0} <span style={{ fontSize: 10, color: '#8c8c8c' }}>/ {dashboardStats.totalLots || 0}</span>
                    </Text>
                  )}
                </div>
              </Card>

              {/* TRANSIT AREA */}
              <div style={{ border: "2px solid #888", borderRadius: 6, padding: 8, background: "#fff", boxShadow: "0 2px 6px rgba(0,0,0,0.05)", marginBottom: 16 }}>
                <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: 8, color: "#444", fontSize: 14 }}>AREA TRANSIT</div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {!loadingTransit && transitList.length === 0 && <div style={{ textAlign: "center", padding: 10 }}><Text type="secondary" style={{ fontSize: 11 }}>Kosong</Text></div>}
                  {transitList.map((out) => (
                    <Card key={out.id_outgoing} hoverable onClick={() => handleClickTransit(out)} style={{ marginBottom: 6, border: "1px solid #ddd", background: "#fafafa", cursor: "pointer" }} bodyStyle={{ padding: 6 }}>
                      <div>
                        <Text strong style={{ fontSize: 10, display: 'block' }}>{out.document_number || "NOPEN -"}</Text>
                        <Text style={{ fontSize: 9, color: '#666' }}>{out.nama_pemilik || "-"}</Text>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* LOADING AREA */}
              <div style={{ border: "2px solid #888", borderRadius: 6, padding: 8, background: "#fff", boxShadow: "0 2px 6px rgba(0,0,0,0.05)" }}>
                <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: 8, color: "#444", fontSize: 14 }}>AREA LOADING</div>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  {!loadingIncoming && incomingList.length === 0 && <div style={{ textAlign: "center", padding: 10 }}><Text type="secondary" style={{ fontSize: 11 }}>Tidak ada incoming</Text></div>}
                  {incomingList.map((inc) => (
                    <Card key={inc.id_incoming} hoverable onClick={() => handleClickIncoming(inc)} style={{ marginBottom: 6, border: "1px solid #ddd", background: "#fafafa" }} bodyStyle={{ padding: 6 }}>
                      <div>
                        <Text strong style={{ fontSize: 10, display: "block" }}>{inc.document_number || "Doc ?"}</Text>
                        <Text style={{ fontSize: 9, color: "#666" }}>{inc.nama_customer || "-"}</Text>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </Col>

        </Row>
      </Card>

      {/* DETAIL MODAL */}
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
        title={modalTitle}
        width="90%"
        style={{ maxWidth: 900 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {renderModalHeader()}
        {renderModalBody()}
      </Modal>

      {/* Spacer for Footer */}
      <div style={{ height: 60 }} />
      <Modal
        title="Detail Stok Barang"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Tutup
          </Button>
        ]}
        width={800}
        centered
      >
        <Table
          columns={stockColumns}
          dataSource={stockData}
          loading={loadingStock}
          rowKey="id_item_stock"
          pagination={{ pageSize: 5 }}
          scroll={{ x: 'max-content' }}
        />
      </Modal>
    </div>
  );
}
